/** Mensajes del backend que todavía salen en inglés → español.
 *
 * Eduardo 2026-09-19: el jugador veía "You are not enrolled in this
 * tournament". El backend tiene ~150 mensajes heredados en inglés repartidos
 * por todos los módulos; en vez de tocarlos uno por uno (y los tests que los
 * citan), se traducen aquí, en el único punto donde el frontend arma el
 * `ApiError`. Un mensaje que ya viene en español pasa tal cual. */

const EXACT: Record<string, string> = {
  "You are not enrolled in this tournament": "No estás inscrito en este torneo.",
  "Player is not enrolled in this tournament": "El jugador no está inscrito en este torneo.",
  "You do not have access to this tournament": "No tienes acceso a este torneo.",
  "You do not have access to this player": "No tienes acceso a este jugador.",
  "You cannot access this notification": "No puedes ver esta notificación.",
  "Requires admin privileges": "Necesitas permisos de administrador.",
  "Requires superadmin privileges": "Necesitas permisos de superadmin.",
  "Requires a player profile": "Necesitas un perfil de jugador.",
  "Insufficient role": "Tu usuario no tiene permiso para hacer esto.",
  "No player profile linked to this user": "Tu usuario no tiene un perfil de jugador ligado.",
  "Invalid username/email or password": "Usuario o contraseña incorrectos.",
  "Invalid or expired token": "Tu sesión caducó. Vuelve a entrar.",
  "Invalid token subject": "Tu sesión no es válida. Vuelve a entrar.",
  "Malformed token roles": "Tu sesión no es válida. Vuelve a entrar.",
  "Missing bearer token": "Necesitas iniciar sesión.",
  "Token issued before password change": "Tu contraseña cambió. Vuelve a entrar.",
  "User is not active": "Este usuario está desactivado.",
  "User no longer exists": "Este usuario ya no existe.",
  "Password must be at least 6 characters": "La contraseña debe tener al menos 6 caracteres.",
  "Admin not found": "Administrador no encontrado.",
  "Target user is not an admin": "Ese usuario no es administrador.",
  "Target user does not have the admin role": "Ese usuario no tiene el rol de administrador.",
  "User is already an admin for this tournament": "Ese usuario ya administra este torneo.",
  "Tournament not found": "Torneo no encontrado.",
  "Tournament not found for this match": "No se encontró el torneo de este partido.",
  "Notification not found": "Notificación no encontrada.",
  "Winner must be one of the match participants": "El ganador debe ser uno de los dos participantes del partido.",
  "A match cannot have the same participant on both sides": "Un partido no puede tener al mismo participante en los dos lados.",
  "A matchup cannot have the same participant on both sides": "Un partido no puede tener al mismo participante en los dos lados.",
  "Participant already used in this jornada/group": "Ese participante ya tiene partido en esta jornada o grupo.",
  "Participant is already dropped": "Ese participante ya está dado de baja.",
  "Participant is not dropped": "Ese participante no está dado de baja.",
  "Participant has no active group assignment": "Ese participante no tiene grupo asignado.",
  "Group name is required": "Escribe el nombre del grupo.",
  "Cannot edit a published or closed jornada": "No se puede editar una jornada publicada o cerrada.",
  "Cannot edit matchups on a published or closed jornada": "No se pueden editar partidos de una jornada publicada o cerrada.",
  "Cannot delete matchups from a published or closed jornada": "No se pueden borrar partidos de una jornada publicada o cerrada.",
  "Cannot delete a matchup that has already been published": "No se puede borrar un partido que ya fue publicado.",
  "Cannot reopen a closed jornada": "No se puede reabrir una jornada cerrada.",
  "Only draft jornadas can be published": "Solo se pueden publicar jornadas en borrador.",
  "Jornadas generation is only available for Escalera tournaments": "Generar jornadas solo aplica a torneos de Escalera.",
  "Cannot change play_mode after participants exist": "No se puede cambiar entre singles y dobles cuando ya hay inscritos.",
  "Cannot accept a rule that has not been published": "Ese reglamento todavía no está publicado.",
  "Rule is already published": "Ese reglamento ya está publicado.",
  "Admin accounts cannot accept rules on a player's behalf.": "Un administrador no puede aceptar el reglamento a nombre de un jugador.",
  "Communication is already published": "Ese aviso ya está publicado.",
  "Audience and channels cannot be modified after publishing": "La audiencia y los canales no se pueden cambiar después de publicar.",
  "Title cannot be empty": "Escribe un título.",
  "Body cannot be empty": "Escribe el mensaje.",
  "Content cannot be empty": "Escribe el contenido.",
  "Round name cannot be empty": "Escribe el nombre de la jornada.",
  "At least one court is required": "Agrega al menos una cancha.",
  "start_time is required": "Indica la hora de inicio.",
  "Could not allocate a unique tournament slug": "No se pudo generar un enlace único para el torneo. Cambia un poco el nombre.",
  "Either player_profile_id or new_player payload is required": "Elige un jugador existente o captura uno nuevo.",
  "Provide either player_profile_id or new_player, not both": "Elige un jugador existente o captura uno nuevo, no ambos.",
  "Request validation failed": "Revisa los datos capturados.",
  "Internal server error": "Ocurrió un error en el servidor. Intenta de nuevo.",
};

const STARTS_WITH: Array<[string, string]> = [
  ["A draft already exists for this tournament", "Ya existe un borrador para este torneo: edítalo en vez de crear otro."],
  ["Cannot delete a completed match", "No se puede borrar un partido ya jugado: cancélalo."],
  ["Cannot delete a published communication", "No se puede borrar un aviso publicado: publica uno nuevo."],
  ["Cannot delete a published rule", "No se puede borrar un reglamento publicado: publica una versión nueva."],
  ["Published rules are immutable", "Un reglamento publicado no se edita: crea un borrador nuevo."],
  ["This rule version is no longer current", "Esta versión del reglamento ya no es la vigente. Recarga la página."],
  ["Group has active participants", "El grupo tiene participantes activos: desactívalo en vez de borrarlo."],
  ["Format config has no rounds", "El formato no tiene jornadas: define primero cuántas son."],
];

const NOT_FOUND_LABEL: Record<string, string> = {
  division: "Categoría no encontrada.",
  group: "Grupo no encontrado.",
  participant: "Participante no encontrado.",
  "team participant": "Pareja no encontrada.",
  match: "Partido no encontrado.",
  matchup: "Partido no encontrado.",
  round: "Jornada no encontrada.",
  rule: "Reglamento no encontrado.",
  report: "Reporte no encontrado.",
  communication: "Aviso no encontrado.",
  tournament: "Torneo no encontrado.",
  user: "Usuario no encontrado.",
  "player profile": "Jugador no encontrado.",
  "player category": "Categoría de jugador no encontrada.",
  "ranking system": "Sistema de ranking no encontrado.",
};

const NOT_FOUND = /^(Team participant|Player profile|Player category|Ranking system|Division|Group|Participant|Matchup|Match|Round|Rule|Report|Communication|Tournament|User) \S+ not found\b/i;
const EMAIL_EXISTS = /^User with email (\S+) already exists$/i;
const UNKNOWN_VALUE = /^Unknown ([a-z_ ]+): (.+)$/i;
const INVALID_FIELD = /^Invalid ([^:]+): (.+)$/i;

export function translateApiMessage(message: string): string {
  const text = (message ?? "").trim();
  if (!text) return message;
  const exact = EXACT[text];
  if (exact) return exact;
  for (const [prefix, translated] of STARTS_WITH) {
    if (text.startsWith(prefix)) return translated;
  }
  const notFound = NOT_FOUND.exec(text);
  if (notFound) return NOT_FOUND_LABEL[notFound[1].toLowerCase()] ?? "Registro no encontrado.";
  const emailExists = EMAIL_EXISTS.exec(text);
  if (emailExists) return `Ya existe un usuario con el correo ${emailExists[1]}.`;
  const unknown = UNKNOWN_VALUE.exec(text);
  if (unknown) return `Valor no reconocido en ${unknown[1]}: ${unknown[2]}`;
  const invalid = INVALID_FIELD.exec(text);
  if (invalid && /must be|expected/.test(invalid[2])) return `Dato inválido en ${invalid[1]}.`;
  return message;
}
